import { z } from 'zod';
import { marketingAttributionV1 } from './marketing-attribution';

// The Webflow script copies the consented attribution cookie to these Intercom contact
// attributes as `vrp_<first|conversion>_<field>` and `vrp_consent_<field>`, and clears
// them all without marketing consent. Click IDs never reach Intercom.
const touchAttributes = {
  touchId: 'touch_id',
  capturedAt: 'touch_time',
  landingPagePath: 'landing_page_path',
  referrerDomain: 'referrer_domain',
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  mixpanelDeviceId: 'mixpanel_device_id',
} as const;

// Contact custom attributes to a validated snapshot; `undefined` without consent attributes.
export const intercomMarketingAttribution = z
  .record(z.string(), z.unknown())
  .transform((attributes): unknown => {
    // Intercom returns cleared attributes as null; an empty value is no evidence.
    const read = (name: string) => {
      const value = attributes[`vrp_${name}`];
      return value === null || value === '' ? undefined : value;
    };
    const touch = (prefix: 'first' | 'conversion') =>
      read(`${prefix}_touch_id`) === undefined
        ? undefined
        : Object.fromEntries(
            Object.entries(touchAttributes).flatMap(([field, name]) => {
              const value = read(`${prefix}_${name}`);
              return value === undefined ? [] : [[field, value]];
            })
          );
    if (read('consent_time') === undefined) return undefined;
    return {
      version: 1,
      firstTouch: touch('first'),
      conversionTouch: touch('conversion'),
      // The script writes attributes only after marketing-storage consent.
      consent: {
        marketingStorage: 'granted',
        adUserData: read('consent_ad_user_data'),
        adPersonalization: read('consent_ad_personalization'),
        policyVersion: read('consent_policy_version'),
        capturedAt: read('consent_time'),
      },
    };
  })
  .pipe(marketingAttributionV1.optional());
